// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Two routes for one subject: the list and one customer's file. The detail page
// is not a modal because it is a place people link each other to, come back to,
// and keep open in a tab while they are on the phone.

export const routes = [
  {
    path: '/customers',
    name: 'customers',
    component: () => import('./pages/CustomersPage.vue'),
    meta: { permission: 'crm:read', title: 'crm.title' },
  },
  // A page and not a modal, on purpose: twenty fields scrolling inside a
  // dialog is a form fighting its frame. The static path wins over /:id by
  // route ranking, so the order here does not matter — but new sits first
  // anyway, for the reader.
  {
    path: '/customers/new',
    name: 'customer-new',
    component: () => import('./pages/CustomerFormPage.vue'),
    meta: { permission: 'crm:write', title: 'crm.form.newTitle' },
  },
  {
    path: '/customers/:id',
    name: 'customer',
    component: () => import('./pages/CustomerPage.vue'),
    meta: { permission: 'crm:read', title: 'crm.customer.title' },
  },
  {
    path: '/customers/:id/edit',
    name: 'customer-edit',
    component: () => import('./pages/CustomerFormPage.vue'),
    meta: { permission: 'crm:write', title: 'crm.form.editTitle' },
  },
]

// Only the list. The detail page is reached from it, from the search box and
// from a link — putting both in the sidebar would give this module two of the
// few entries the next module installed has the same claim to.
export const navigation = [
  { to: '/customers', labelKey: 'crm.title', permission: 'crm:read', icon: 'contact', group: 'business' },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (section 16): a module that ships two is a module that cannot be
// sold to the third client.
const baseMessages = {
  es: {
    crm: {
      title: 'Clientes',
      subtitle: 'La ficha de cada cliente al que vendéis, sea empresa o particular',
      // La explicación entera del módulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aquí sólo van
      // las dos frases que el índice de la ayuda necesita antes de abrirlo.
      help: {
        title: 'Clientes',
        subtitle: 'Qué guarda la ficha de un cliente, por qué no se borra y de dónde salen sus números',
      },
      dashboard: {
        activeCustomers: 'Clientes activos',
        activeCustomersHelp: 'Cuántas fichas de cliente están en activo ahora mismo, sin contar las archivadas.',
        pendingAmount: 'Pendiente de cobro',
        pendingAmountHelp: 'Suma de lo facturado a clientes y todavía no cobrado, en euros.',
        latestCustomers: 'Últimos clientes',
        latestCustomersHelp: 'Las fichas abiertas más recientemente, de la más nueva a la más antigua. Estira la tarjeta y caben más.',
      },
      actions: {
        new: 'Nuevo cliente', import: 'Importar', edit: 'Editar',
        archive: 'Archivar', cancel: 'Cancelar', save: 'Guardar', remove: 'Eliminar', close: 'Cerrar',
        restore: 'Devolver a las listas',
      },
      filters: {
        search: 'Buscar', searchPlaceholder: 'Nombre, NIF, población…',
        status: 'Estado', allStatuses: 'Todos',
        kind: 'Tipo', allKinds: 'Todos',
        sector: 'Sector', allSectors: 'Todos',
        archived: 'Archivados', archivedNo: 'Sin archivados',
        archivedOnly: 'Solo archivados', archivedAll: 'Todos',
      },
      status: { prospect: 'Potencial', active: 'Activo', inactive: 'Inactivo' },
      kinds: { company: 'Empresa', individual: 'Particular' },
      // La lista mezcla empresas y particulares, así que sus cabeceras no
      // pueden decir «Empresa» ni «CIF»: bajo esa columna hay también DNIs.
      table: {
        name: 'Cliente', sector: 'Sector', city: 'Población',
        contact: 'Contacto', pending: 'Pendiente', status: 'Estado',
      },
      kpi: { active: 'Activos', prospects: 'Potenciales', total: 'Total', pending: 'Pendiente de cobro' },
      empty: {
        none: 'Todavía no hay clientes',
        noneHint: 'Da de alta el primero, o trae los que ya tenéis desde una hoja de cálculo.',
        filtered: 'Sin resultados',
        filteredHint: 'Prueba a quitar algún filtro o a buscar otra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Siguiente', of: 'Página {page} de {pages}' },
      customer: {
        title: 'Ficha de cliente',
        loading: 'Cargando…',
        details: 'Datos de contacto',
        terms: 'Condiciones comerciales',
        billingAddress: 'Dirección de facturación',
        shippingAddress: 'Dirección de envío',
        differentShipping: 'El envío va a otra dirección',
        noCreditLimit: 'Sin límite',
        fiscal: 'Fiscalidad',
      },
      // El sector se guarda como código y se enseña traducido. Un código que
      // no esté aquí sale tal cual venía: es de una importación que no se pudo
      // encajar en la lista, y enseñarlo tal cual es lo que hace que alguien
      // lo corrija.
      paymentTerms: {
        immediate: 'Al contado',
        prepaid: 'Pago por adelantado',
        on_delivery: 'Contra entrega',
        '15_days': '15 días',
        '30_days': '30 días',
        '45_days': '45 días',
        '60_days': '60 días',
        '30_eom': '30 días fin de mes',
        '60_eom': '60 días fin de mes',
        '50_50': '50 % por adelantado, 50 % a la entrega',
        other: 'Otra',
      },
      paymentMethods: {
        transfer: 'Transferencia bancaria',
        direct_debit: 'Domiciliación bancaria',
        card: 'Tarjeta',
        cash: 'Efectivo',
        cheque: 'Cheque',
        promissory_note: 'Pagaré',
        confirming: 'Confirming',
        other: 'Otro',
      },
      taxRegimes: {
        general: 'Régimen general',
        equivalence_surcharge: 'Recargo de equivalencia',
        exempt: 'Exenta de IVA (art. 20 LIVA)',
        intra_community: 'Intracomunitario',
        export: 'Exportación (fuera de la UE)',
        reverse_charge: 'Inversión del sujeto pasivo',
      },
      sectors: {
        agriculture: 'Agricultura, ganadería y pesca',
        automotive: 'Automoción',
        'banking-insurance': 'Banca y seguros',
        'chemicals-plastics': 'Química y plásticos',
        construction: 'Construcción y obra civil',
        consulting: 'Consultoría y servicios profesionales',
        education: 'Educación y formación',
        'energy-utilities': 'Energía y suministros',
        'food-beverage': 'Alimentación y bebidas',
        'health-social': 'Salud y servicios sociales',
        hospitality: 'Hostelería y restauración',
        installations: 'Instalaciones y mantenimiento',
        legal: 'Servicios jurídicos',
        'logistics-transport': 'Logística y transporte',
        manufacturing: 'Industria y fabricación',
        'marketing-media': 'Marketing, publicidad y medios',
        metal: 'Metalurgia y metalmecánica',
        'pharma-biotech': 'Farmacia y biotecnología',
        'public-sector': 'Sector público y administración',
        'real-estate': 'Inmobiliario',
        retail: 'Comercio minorista',
        'sports-leisure': 'Deporte y ocio',
        technology: 'Tecnología y software',
        'textile-fashion': 'Textil, moda y calzado',
        'tourism-travel': 'Turismo y viajes',
        wholesale: 'Comercio mayorista y distribución',
        other: 'Otros',
      },
      fields: {
        taxRegime: 'Régimen de IVA',
        taxRegimeHint: 'Decide si la factura lleva IVA y a qué tipo',
        vatNumber: 'NIF-IVA europeo',
        vatNumberHint: 'Solo si le vendéis desde otro país de la UE',
        vatNumberNeeded: 'Falta: un intracomunitario sin NIF-IVA no se sostiene en una inspección',
        iban: 'IBAN',
        ibanHint: 'La cuenta del cliente, para domiciliarle los recibos',
        vatNumberPlaceholder: 'ESB12345678',
        ibanPlaceholder: 'ES91 2100 0418 4502 0005 1332',
        withholdingRate: 'Retención de IRPF',
        withholdingRateHint: 'En porcentaje. 0 en casi todos los clientes',
        name: 'Razón social', namePlaceholder: 'Nombre completo de la empresa',
        personName: 'Nombre y apellidos', personNamePlaceholder: 'Nombre completo de la persona',
        shortName: 'Nombre corto', shortNameHint: 'El que usáis en las listas',
        kind: 'Tipo de cliente', kindHint: 'A un particular no se le piden sector ni web',
        taxId: 'CIF / NIF', personTaxId: 'DNI / NIE',
        status: 'Estado', sector: 'Sector', noSector: 'Sin sector',
        noPaymentTerms: 'Sin definir', noPaymentMethod: 'Sin definir',
        email: 'Correo', phone: 'Teléfono', website: 'Web',
        address: 'Calle', postalCode: 'CP', city: 'Población', town: 'Población', country: 'País',
        creditLimit: 'Límite de crédito', creditLimitHint: 'Déjalo en 0 si no le habéis puesto tope',
        paymentTerms: 'Forma de pago', paymentMethod: 'Medio de pago', currency: 'Moneda',
        rating: 'Valoración', noRating: 'Sin valorar', notes: 'Notas',
        createdAt: 'Alta', lastActivity: 'Última actividad',
        contactName: 'Nombre', contactPosition: 'Cargo',
        contactPositionPlaceholder: 'Ej.: Director técnico',
      },
      tabs: { overview: 'Ficha', history: 'Historial' },
      // Lo que ha pasado con este cliente, contado por el módulo que lo hizo.
      // Cada clave es el nombre del evento tal cual: la frase se escribe aquí y
      // nunca en la base de datos, que no habla ningún idioma (§16).
      movements: {
        title: 'Historial de movimientos',
        quote: { created: 'Presupuesto creado', accepted: 'Presupuesto aceptado' },
        work_order: { created: 'Orden de trabajo abierta', completed: 'Orden de trabajo completada', cancelled: 'Orden de trabajo anulada' },
        invoice: { issued: 'Factura emitida', payment_registered: 'Cobro anotado', paid: 'Factura cobrada', cancelled: 'Factura anulada' },
      },
      activity: {
        quotes: 'Presupuestos', accepted: 'Aceptados', openWorkOrders: 'OTs abiertas',
        invoiced: 'Facturado', pending: 'Pendiente',
      },
      contacts: {
        title: 'Personas de contacto',
        add: 'Añadir contacto', addTitle: 'Añadir contacto', editTitle: 'Editar contacto',
        none: 'Todavía no hay contactos',
        noneHint: 'La persona a quien llamáis cuando pasa algo con este cliente.',
        primary: 'Principal', makePrimary: 'Hacer principal', markPrimary: 'Marcar como contacto principal',
        primaryHint: 'Es el nombre que sale en los documentos. Solo puede haber uno.',
        firstIsPrimary: 'El primero de la lista es el contacto principal.',
        removeTitle: 'Eliminar contacto', removeBody: '¿Eliminar a {name} de este cliente?',
        removed: 'Contacto eliminado', saved: 'Contacto guardado',
      },
      form: {
        newTitle: 'Nuevo cliente', editTitle: 'Editar cliente',
        company: 'Datos de la empresa', person: 'Datos de la persona',
        newHint: 'Solo el nombre es obligatorio; el resto se puede completar después',
        created: 'Cliente dado de alta', saved: 'Cliente guardado',
      },
      archive: {
        title: 'Archivar cliente',
        body: 'Vas a archivar a {name}.',
        keeps: 'No se borra nada: deja de aparecer en las listas y su historial sigue intacto.',
        done: 'Cliente archivado',
        isArchived: 'Este cliente está archivado: no sale en ninguna lista ni se puede elegir en un presupuesto. Devuélvelo con el botón de arriba.',
        restored: 'Cliente devuelto a las listas',
      },
      import: {
        title: 'Importar clientes',
        file: 'Archivo', fileHint: 'Una hoja de cálculo (.xlsx, .xls o .csv) con un cliente por fila.',
        sheet: 'Hoja', sheetOption: '{name} ({rows} filas)',
        mapHint: 'Di qué columna es cada cosa. Se importarán {rows} filas.',
        ignore: 'No importar', column: 'Columna {index}',
        preview: 'Primeras filas',
        duplicatesNote: 'Las filas cuyo NIF ya esté en la base de datos se saltan, no se duplican.',
        run: 'Importar {rows} filas',
        created: 'Creados', skipped: 'Saltados', failed: 'Con error',
        errorsTitle: 'Filas que no entraron', row: 'Fila {row}:',
        warningsTitle: 'Filas que entraron sin un dato', dropped: 'se importó sin {field}, porque el que traía no es válido',
        done: '{count} clientes importados',
        nameRequired: 'Falta decir qué columna tiene el nombre del cliente',
        fields: {
          name: 'Nombre o razón social', shortName: 'Nombre corto', taxId: 'NIF',
          email: 'Correo', phone: 'Teléfono', website: 'Web',
          address: 'Dirección', city: 'Población', postalCode: 'CP', country: 'País',
          shippingAddress: 'Dirección de envío', sector: 'Sector', notes: 'Notas',
          iban: 'IBAN', vatNumber: 'NIF-IVA europeo',
        },
      },
      search: { subtitle: 'Ver su ficha' },
    },

    // ── Lo que este módulo se llama en las pantallas de la plataforma ───────
    // Roles, ajustes y auditoría son de la plataforma, pero los textos que
    // hablan de clientes son de este módulo. Van aquí para que instalarlo deje
    // esas tres pantallas completas sin tocar la plataforma.
    modules: { crm: 'Clientes' },
    // Las aperturas que este módulo aporta al panel del asistente. La clave es
    // el id estable registrado en backend/assistant.js; el texto inglés de allí
    // es solo el fallback.
    assistant: {
      openers: {
        crm: {
          'how-is-the-customer-base': '¿Cómo va nuestra cartera de clientes?',
          'which-prospects': '¿Qué clientes potenciales tenemos ahora mismo?',
          'file-a-customer': 'Da de alta un cliente con el que acabo de hablar',
          'raise-a-credit-limit': 'Súbele el límite de crédito a un cliente',
        },
      },
    },
    permissions: {
      crm: {
        read: 'Ver la ficha de los clientes',
        write: 'Crear y editar clientes y contactos',
        archive: 'Archivar clientes',
        import: 'Importar clientes desde hoja de cálculo',
      },
    },
    settings: {
      ns: {
        crm: {
          title: 'Clientes',
          subtitle: 'Con qué datos nace un cliente nuevo',
          defaultCountry: { label: 'País con el que nace un cliente nuevo' },
          defaultCurrency: {
            label: 'Moneda con la que se factura a un cliente nuevo',
            help: 'ISO 4217, tres letras. Después se puede cambiar cliente a cliente.',
          },
          defaultPaymentTerms: { label: 'Forma de pago con la que nace un cliente nuevo' },
          defaultPaymentMethod: { label: 'Medio de pago con el que nace un cliente nuevo' },
          defaultTaxRegime: {
            label: 'Régimen de IVA con el que nace un cliente nuevo',
            help: 'Déjalo en general salvo que casi todo lo que vendéis sea exento o intracomunitario.',
          },
          requireTaxId: {
            label: 'Exigir CIF/NIF en todos los clientes',
            help: 'Déjalo desactivado si dais de alta potenciales antes de pedírselo.',
          },
          defaultToIndividual: {
            label: 'Los clientes nuevos nacen como particulares',
            help: 'Actívalo si vendéis al público. En cualquier caso se puede cambiar cliente a cliente.',
          },
        },
      },
    },
    audit: {
      actions: {
        crm: {
          customer_created: 'Cliente dado de alta',
          customer_updated: 'Cliente modificado',
          customer_archived: 'Cliente archivado',
          customer_restored: 'Cliente devuelto de archivados',
          contact_added: 'Contacto añadido',
          contact_updated: 'Contacto modificado',
          contact_removed: 'Contacto eliminado',
          customers_imported: 'Clientes importados desde hoja de cálculo',
        },
      },
    },
  },

  ca: {
    crm: {
      title: 'Clients',
      subtitle: 'La fitxa de cada client a qui veneu, sigui empresa o particular',
      // La explicación entera del módulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aquí sólo van
      // las dos frases que el índice de la ayuda necesita antes de abrirlo.
      help: {
        title: 'Clients',
        subtitle: 'Què guarda la fitxa d’un client, per què no s’esborra i d’on surten els seus números',
      },
      dashboard: {
        activeCustomers: 'Clients actius',
        activeCustomersHelp: 'Quantes fitxes de client són actives ara mateix, sense comptar les arxivades.',
        pendingAmount: 'Pendent de cobrament',
        pendingAmountHelp: 'Suma del que heu facturat a clients i encara no heu cobrat, en euros.',
        latestCustomers: 'Últims clients',
        latestCustomersHelp: 'Les fitxes obertes més recentment, de la més nova a la més antiga. Estira la targeta i n\'hi caben més.',
      },
      actions: {
        new: 'Nou client', import: 'Importar', edit: 'Editar',
        archive: 'Arxivar', cancel: 'Cancel·lar', save: 'Desar', remove: 'Eliminar', close: 'Tancar',
        restore: 'Tornar a les llistes',
      },
      filters: {
        search: 'Cercar', searchPlaceholder: 'Nom, NIF, població…',
        status: 'Estat', allStatuses: 'Tots',
        kind: 'Tipus', allKinds: 'Tots',
        sector: 'Sector', allSectors: 'Tots',
        archived: 'Arxivats', archivedNo: 'Sense arxivats',
        archivedOnly: 'Només arxivats', archivedAll: 'Tots',
      },
      status: { prospect: 'Potencial', active: 'Actiu', inactive: 'Inactiu' },
      kinds: { company: 'Empresa', individual: 'Particular' },
      // La llista barreja empreses i particulars: la capçalera no pot dir
      // «Empresa» quan sota hi ha també persones.
      table: {
        name: 'Client', sector: 'Sector', city: 'Població',
        contact: 'Contacte', pending: 'Pendent', status: 'Estat',
      },
      kpi: { active: 'Actius', prospects: 'Potencials', total: 'Total', pending: 'Pendent de cobrament' },
      empty: {
        none: 'Encara no hi ha clients',
        noneHint: "Dona d'alta el primer, o porta els que ja teniu des d'un full de càlcul.",
        filtered: 'Sense resultats',
        filteredHint: 'Prova de treure algun filtre o de cercar una altra cosa.',
      },
      pager: { previous: 'Anterior', next: 'Següent', of: 'Pàgina {page} de {pages}' },
      customer: {
        title: 'Fitxa de client',
        loading: 'Carregant…',
        details: 'Dades de contacte',
        terms: 'Condicions comercials',
        billingAddress: 'Adreça de facturació',
        shippingAddress: 'Adreça d\'enviament',
        differentShipping: "L'enviament va a una altra adreça",
        noCreditLimit: 'Sense límit',
        fiscal: 'Fiscalitat',
      },
      // El sector es desa com a codi i s'ensenya traduït. Un codi que no sigui
      // aquí surt tal com venia: és d'una importació que no s'ha pogut encaixar
      // a la llista, i ensenyar-lo tal qual és el que fa que algú el corregeixi.
      paymentTerms: {
        immediate: 'Al comptat',
        prepaid: 'Pagament per avançat',
        on_delivery: 'Contra lliurament',
        '15_days': '15 dies',
        '30_days': '30 dies',
        '45_days': '45 dies',
        '60_days': '60 dies',
        '30_eom': '30 dies fi de mes',
        '60_eom': '60 dies fi de mes',
        '50_50': '50 % per avançat, 50 % al lliurament',
        other: 'Altra',
      },
      paymentMethods: {
        transfer: 'Transferència bancària',
        direct_debit: 'Domiciliació bancària',
        card: 'Targeta',
        cash: 'Efectiu',
        cheque: 'Xec',
        promissory_note: 'Pagaré',
        confirming: 'Confirming',
        other: 'Altre',
      },
      taxRegimes: {
        general: 'Règim general',
        equivalence_surcharge: 'Recàrrec d’equivalència',
        exempt: 'Exempta d’IVA (art. 20 LIVA)',
        intra_community: 'Intracomunitari',
        export: 'Exportació (fora de la UE)',
        reverse_charge: 'Inversió del subjecte passiu',
      },
      sectors: {
        agriculture: 'Agricultura, ramaderia i pesca',
        automotive: 'Automoció',
        'banking-insurance': 'Banca i assegurances',
        'chemicals-plastics': 'Química i plàstics',
        construction: 'Construcció i obra civil',
        consulting: 'Consultoria i serveis professionals',
        education: 'Educació i formació',
        'energy-utilities': 'Energia i subministraments',
        'food-beverage': 'Alimentació i begudes',
        'health-social': 'Salut i serveis socials',
        hospitality: 'Hostaleria i restauració',
        installations: 'Instal·lacions i manteniment',
        legal: 'Serveis jurídics',
        'logistics-transport': 'Logística i transport',
        manufacturing: 'Indústria i fabricació',
        'marketing-media': 'Màrqueting, publicitat i mitjans',
        metal: 'Metal·lúrgia i mecànica',
        'pharma-biotech': 'Farmàcia i biotecnologia',
        'public-sector': 'Sector públic i administració',
        'real-estate': 'Immobiliari',
        retail: 'Comerç minorista',
        'sports-leisure': 'Esport i lleure',
        technology: 'Tecnologia i programari',
        'textile-fashion': 'Tèxtil, moda i calçat',
        'tourism-travel': 'Turisme i viatges',
        wholesale: 'Comerç majorista i distribució',
        other: 'Altres',
      },
      fields: {
        taxRegime: 'Règim d’IVA',
        taxRegimeHint: 'Decideix si la factura porta IVA i a quin tipus',
        vatNumber: 'NIF-IVA europeu',
        vatNumberHint: 'Només si li veneu des d’un altre país de la UE',
        vatNumberNeeded: 'Falta: un intracomunitari sense NIF-IVA no se sosté en una inspecció',
        iban: 'IBAN',
        ibanHint: 'El compte del client, per domiciliar-li els rebuts',
        vatNumberPlaceholder: 'ESB12345678',
        ibanPlaceholder: 'ES91 2100 0418 4502 0005 1332',
        withholdingRate: 'Retenció d’IRPF',
        withholdingRateHint: 'En percentatge. 0 en gairebé tots els clients',
        name: 'Raó social', namePlaceholder: "Nom complet de l'empresa",
        personName: 'Nom i cognoms', personNamePlaceholder: 'Nom complet de la persona',
        shortName: 'Nom curt', shortNameHint: 'El que feu servir a les llistes',
        kind: 'Tipus de client', kindHint: 'A un particular no se li demanen sector ni web',
        taxId: 'NIF', personTaxId: 'DNI / NIE',
        status: 'Estat', sector: 'Sector', noSector: 'Sense sector',
        noPaymentTerms: 'Sense definir', noPaymentMethod: 'Sense definir',
        email: 'Correu', phone: 'Telèfon', website: 'Web',
        address: 'Carrer', postalCode: 'CP', city: 'Població', town: 'Població', country: 'País',
        creditLimit: 'Límit de crèdit', creditLimitHint: 'Deixa-ho a 0 si no li heu posat sostre',
        paymentTerms: 'Forma de pagament', paymentMethod: 'Mitjà de pagament', currency: 'Moneda',
        rating: 'Valoració', noRating: 'Sense valorar', notes: 'Notes',
        createdAt: 'Alta', lastActivity: 'Última activitat',
        contactName: 'Nom', contactPosition: 'Càrrec',
        contactPositionPlaceholder: 'Ex.: Director tècnic',
      },
      tabs: { overview: 'Fitxa', history: 'Historial' },
      movements: {
        title: 'Historial de moviments',
        quote: { created: 'Pressupost creat', accepted: 'Pressupost acceptat' },
        work_order: { created: 'Ordre de treball oberta', completed: 'Ordre de treball completada', cancelled: 'Ordre de treball anul·lada' },
        invoice: { issued: 'Factura emesa', payment_registered: 'Cobrament anotat', paid: 'Factura cobrada', cancelled: 'Factura anul·lada' },
      },
      activity: {
        quotes: 'Pressupostos', accepted: 'Acceptats', openWorkOrders: 'OTs obertes',
        invoiced: 'Facturat', pending: 'Pendent',
      },
      contacts: {
        title: 'Persones de contacte',
        add: 'Afegir contacte', addTitle: 'Afegir contacte', editTitle: 'Editar contacte',
        none: 'Encara no hi ha contactes',
        noneHint: 'La persona a qui truqueu quan passa alguna cosa amb aquest client.',
        primary: 'Principal', makePrimary: 'Fer principal', markPrimary: 'Marcar com a contacte principal',
        primaryHint: 'És el nom que surt als documents. Només n\'hi pot haver un.',
        firstIsPrimary: 'El primer de la llista és el contacte principal.',
        removeTitle: 'Eliminar contacte', removeBody: 'Vols eliminar {name} d\'aquest client?',
        removed: 'Contacte eliminat', saved: 'Contacte desat',
      },
      form: {
        newTitle: 'Nou client', editTitle: 'Editar client',
        company: "Dades de l'empresa", person: 'Dades de la persona',
        newHint: 'Només cal el nom; la resta es pot completar després',
        created: "Client donat d'alta", saved: 'Client desat',
      },
      archive: {
        title: 'Arxivar client',
        body: 'Vas a arxivar {name}.',
        keeps: "No s'esborra res: deixa d'aparèixer a les llistes i el seu historial queda intacte.",
        done: 'Client arxivat',
        isArchived: 'Aquest client està arxivat: no surt en cap llista ni es pot triar en un pressupost. Torna’l amb el botó de dalt.',
        restored: 'Client tornat a les llistes',
      },
      import: {
        title: 'Importar clients',
        file: 'Fitxer', fileHint: 'Un full de càlcul (.xlsx, .xls o .csv) amb un client per fila.',
        sheet: 'Full', sheetOption: '{name} ({rows} files)',
        mapHint: 'Digues quina columna és cada cosa. S\'importaran {rows} files.',
        ignore: 'No importar', column: 'Columna {index}',
        preview: 'Primeres files',
        duplicatesNote: 'Les files amb un NIF que ja hi és es salten, no es dupliquen.',
        run: 'Importar {rows} files',
        created: 'Creats', skipped: 'Saltats', failed: 'Amb error',
        errorsTitle: 'Files que no van entrar', row: 'Fila {row}:',
        warningsTitle: 'Files que van entrar sense una dada', dropped: 's’ha importat sense {field}, perquè el que portava no és vàlid',
        done: '{count} clients importats',
        nameRequired: 'Falta dir quina columna té el nom del client',
        fields: {
          name: 'Nom o raó social', shortName: 'Nom curt', taxId: 'NIF',
          email: 'Correu', phone: 'Telèfon', website: 'Web',
          address: 'Adreça', city: 'Població', postalCode: 'CP', country: 'País',
          shippingAddress: 'Adreça d\'enviament', sector: 'Sector', notes: 'Notes',
          iban: 'IBAN', vatNumber: 'NIF-IVA europeu',
        },
      },
      search: { subtitle: 'Veure la seva fitxa' },
    },

    modules: { crm: 'Clients' },
    assistant: {
      openers: {
        crm: {
          'how-is-the-customer-base': 'Com va la nostra cartera de clients?',
          'which-prospects': 'Quins clients potencials tenim ara mateix?',
          'file-a-customer': "Dona d'alta un client amb qui acabo de parlar",
          'raise-a-credit-limit': "Puja el límit de crèdit d'un client",
        },
      },
    },
    permissions: {
      crm: {
        read: 'Veure la fitxa dels clients',
        write: 'Crear i editar clients i contactes',
        archive: 'Arxivar clients',
        import: 'Importar clients des de full de càlcul',
      },
    },
    settings: {
      ns: {
        crm: {
          title: 'Clients',
          subtitle: 'Amb quines dades neix un client nou',
          defaultCountry: { label: 'País amb què neix un client nou' },
          defaultCurrency: {
            label: 'Moneda amb què es factura un client nou',
            help: 'ISO 4217, tres lletres. Després es pot canviar client a client.',
          },
          defaultPaymentTerms: { label: 'Forma de pagament amb què neix un client nou' },
          defaultPaymentMethod: { label: 'Mitjà de pagament amb què neix un client nou' },
          defaultTaxRegime: {
            label: 'Règim d’IVA amb què neix un client nou',
            help: 'Deixa’l en general llevat que gairebé tot el que veneu sigui exempt o intracomunitari.',
          },
          requireTaxId: {
            label: 'Exigir NIF a tots els clients',
            help: "Deixa-ho desactivat si doneu d'alta potencials abans de demanar-l'hi.",
          },
          defaultToIndividual: {
            label: 'Els clients nous neixen com a particulars',
            help: 'Activa-ho si veneu al públic. En tot cas es pot canviar client a client.',
          },
        },
      },
    },
    audit: {
      actions: {
        crm: {
          customer_created: "Client donat d'alta",
          customer_updated: 'Client modificat',
          customer_archived: 'Client arxivat',
          customer_restored: 'Client tornat d’arxivats',
          contact_added: 'Contacte afegit',
          contact_updated: 'Contacte modificat',
          contact_removed: 'Contacte eliminat',
          customers_imported: 'Clients importats des de full de càlcul',
        },
      },
    },
  },

  en: {
    crm: {
      title: 'Customers',
      subtitle: 'The file on everyone you sell to, company or private individual',
      // La explicación entera del módulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aquí sólo van
      // las dos frases que el índice de la ayuda necesita antes de abrirlo.
      help: {
        title: 'Customers',
        subtitle: 'What a customer file holds, why it is never deleted, and where its numbers come from',
      },
      dashboard: {
        activeCustomers: 'Active customers',
        activeCustomersHelp: 'How many customer files are active right now, archived ones excluded.',
        pendingAmount: 'Pending collection',
        pendingAmountHelp: 'What has been invoiced to customers and not yet collected, in euros.',
        latestCustomers: 'Latest customers',
        latestCustomersHelp: 'The most recently opened files, newest first. Stretch the card and more fit.',
      },
      actions: {
        new: 'New customer', import: 'Import', edit: 'Edit',
        archive: 'Archive', cancel: 'Cancel', save: 'Save', remove: 'Remove', close: 'Close',
        restore: 'Restore',
      },
      filters: {
        search: 'Search', searchPlaceholder: 'Name, tax id, town…',
        status: 'Status', allStatuses: 'All',
        kind: 'Type', allKinds: 'All',
        sector: 'Sector', allSectors: 'All',
        archived: 'Archived', archivedNo: 'Not archived',
        archivedOnly: 'Archived only', archivedAll: 'All',
      },
      status: { prospect: 'Prospect', active: 'Active', inactive: 'Inactive' },
      kinds: { company: 'Company', individual: 'Private individual' },
      // The list holds companies and private individuals alike, so its heading
      // cannot say "Company" over a column with people in it.
      table: {
        name: 'Customer', sector: 'Sector', city: 'Town',
        contact: 'Contact', pending: 'Outstanding', status: 'Status',
      },
      kpi: { active: 'Active', prospects: 'Prospects', total: 'Total', pending: 'Outstanding' },
      empty: {
        none: 'No customers yet',
        noneHint: 'Add the first one, or bring in the ones you already have from a spreadsheet.',
        filtered: 'Nothing found',
        filteredHint: 'Try clearing a filter or searching for something else.',
      },
      pager: { previous: 'Previous', next: 'Next', of: 'Page {page} of {pages}' },
      customer: {
        title: 'Customer file',
        loading: 'Loading…',
        details: 'Contact details',
        terms: 'Commercial terms',
        billingAddress: 'Billing address',
        shippingAddress: 'Delivery address',
        differentShipping: 'Deliveries go somewhere else',
        noCreditLimit: 'No limit',
        fiscal: 'Tax',
      },
      // The sector is stored as a code and shown translated. A code that is not
      // here is shown exactly as it arrived: it came from an import that could
      // not be matched to the list, and showing it is what gets it corrected.
      paymentTerms: {
        immediate: 'Immediate',
        prepaid: 'Paid in advance',
        on_delivery: 'On delivery',
        '15_days': '15 days',
        '30_days': '30 days',
        '45_days': '45 days',
        '60_days': '60 days',
        '30_eom': '30 days, end of month',
        '60_eom': '60 days, end of month',
        '50_50': '50 % up front, 50 % on delivery',
        other: 'Other',
      },
      paymentMethods: {
        transfer: 'Bank transfer',
        direct_debit: 'Direct debit',
        card: 'Card',
        cash: 'Cash',
        cheque: 'Cheque',
        promissory_note: 'Promissory note',
        confirming: 'Confirming',
        other: 'Other',
      },
      taxRegimes: {
        general: 'Standard VAT',
        equivalence_surcharge: 'Equivalence surcharge',
        exempt: 'VAT exempt',
        intra_community: 'Intra-community',
        export: 'Export (outside the EU)',
        reverse_charge: 'Reverse charge',
      },
      sectors: {
        agriculture: 'Agriculture, livestock and fishing',
        automotive: 'Automotive',
        'banking-insurance': 'Banking and insurance',
        'chemicals-plastics': 'Chemicals and plastics',
        construction: 'Construction and civil works',
        consulting: 'Consulting and professional services',
        education: 'Education and training',
        'energy-utilities': 'Energy and utilities',
        'food-beverage': 'Food and drink',
        'health-social': 'Health and social care',
        hospitality: 'Hospitality and catering',
        installations: 'Installations and maintenance',
        legal: 'Legal services',
        'logistics-transport': 'Logistics and transport',
        manufacturing: 'Industry and manufacturing',
        'marketing-media': 'Marketing, advertising and media',
        metal: 'Metal and engineering',
        'pharma-biotech': 'Pharmaceuticals and biotech',
        'public-sector': 'Public sector and government',
        'real-estate': 'Real estate',
        retail: 'Retail',
        'sports-leisure': 'Sport and leisure',
        technology: 'Technology and software',
        'textile-fashion': 'Textiles, fashion and footwear',
        'tourism-travel': 'Tourism and travel',
        wholesale: 'Wholesale and distribution',
        other: 'Other',
      },
      fields: {
        taxRegime: 'VAT regime',
        taxRegimeHint: 'Decides whether the invoice carries VAT, and at what rate',
        vatNumber: 'EU VAT number',
        vatNumberHint: 'Only if you sell to them from another EU country',
        vatNumberNeeded: 'Missing: an intra-community sale with no VAT number will not survive an audit',
        iban: 'IBAN',
        ibanHint: 'The customer’s account, for collecting by direct debit',
        vatNumberPlaceholder: 'ESB12345678',
        ibanPlaceholder: 'ES91 2100 0418 4502 0005 1332',
        withholdingRate: 'Withholding tax',
        withholdingRateHint: 'As a percentage. 0 for almost every customer',
        name: 'Registered name', namePlaceholder: "The company's full legal name",
        personName: 'Full name', personNamePlaceholder: "The person's full name",
        shortName: 'Short name', shortNameHint: 'What you call them in a list',
        kind: 'Type of customer', kindHint: 'A private individual is not asked for a sector or a website',
        taxId: 'Tax id', personTaxId: 'ID number',
        status: 'Status', sector: 'Sector', noSector: 'No sector',
        noPaymentTerms: 'Not agreed', noPaymentMethod: 'Not agreed',
        email: 'Email', phone: 'Phone', website: 'Website',
        address: 'Street', postalCode: 'Postcode', city: 'Town', town: 'Town', country: 'Country',
        creditLimit: 'Credit limit', creditLimitHint: 'Leave it at 0 if you have not set one',
        paymentTerms: 'Payment terms', paymentMethod: 'Payment method', currency: 'Currency',
        rating: 'Rating', noRating: 'Not rated', notes: 'Notes',
        createdAt: 'On file since', lastActivity: 'Last activity',
        contactName: 'Name', contactPosition: 'Position',
        contactPositionPlaceholder: 'e.g. Technical director',
      },
      tabs: { overview: 'Details', history: 'History' },
      movements: {
        title: 'What has happened',
        quote: { created: 'Quote drafted', accepted: 'Quote accepted' },
        work_order: { created: 'Work order opened', completed: 'Work order completed', cancelled: 'Work order cancelled' },
        invoice: { issued: 'Invoice issued', payment_registered: 'Payment registered', paid: 'Invoice paid', cancelled: 'Invoice cancelled' },
      },
      activity: {
        quotes: 'Quotes', accepted: 'Accepted', openWorkOrders: 'Open work orders',
        invoiced: 'Invoiced', pending: 'Outstanding',
      },
      contacts: {
        title: 'People',
        add: 'Add a contact', addTitle: 'Add a contact', editTitle: 'Edit contact',
        none: 'No contacts yet',
        noneHint: 'The person you call when something happens with this customer.',
        primary: 'Primary', makePrimary: 'Make primary', markPrimary: 'Make this the primary contact',
        primaryHint: 'This is the name that goes on documents. There can only be one.',
        firstIsPrimary: 'The first on the list is the primary contact.',
        removeTitle: 'Remove contact', removeBody: 'Remove {name} from this customer?',
        removed: 'Contact removed', saved: 'Contact saved',
      },
      form: {
        newTitle: 'New customer', editTitle: 'Edit customer',
        company: 'Company details', person: 'Personal details',
        newHint: 'Only the name is required; the rest can be filled in later',
        created: 'Customer added', saved: 'Customer saved',
      },
      archive: {
        title: 'Archive customer',
        body: 'You are about to archive {name}.',
        keeps: 'Nothing is deleted: they stop appearing in lists and their history stays intact.',
        done: 'Customer archived',
        isArchived: 'This customer is archived: they are on no list and cannot be picked on a quote. Bring them back with the button above.',
        restored: 'Customer is back on the lists',
      },
      import: {
        title: 'Import customers',
        file: 'File', fileHint: 'A spreadsheet (.xlsx, .xls or .csv) with one customer per row.',
        sheet: 'Sheet', sheetOption: '{name} ({rows} rows)',
        mapHint: 'Say which column is which. {rows} rows will be imported.',
        ignore: 'Do not import', column: 'Column {index}',
        preview: 'First rows',
        duplicatesNote: 'Rows whose tax id is already on file are skipped, not duplicated.',
        run: 'Import {rows} rows',
        created: 'Created', skipped: 'Skipped', failed: 'Failed',
        errorsTitle: 'Rows that did not go in', row: 'Row {row}:',
        warningsTitle: 'Rows that went in without one field', dropped: 'imported without its {field}, because the one on the sheet is not valid',
        done: '{count} customers imported',
        nameRequired: 'Say which column holds the customer name',
        fields: {
          name: 'Name or registered name', shortName: 'Short name', taxId: 'Tax id',
          email: 'Email', phone: 'Phone', website: 'Website',
          address: 'Address', city: 'Town', postalCode: 'Postcode', country: 'Country',
          shippingAddress: 'Delivery address', sector: 'Sector', notes: 'Notes',
          iban: 'IBAN', vatNumber: 'EU VAT number',
        },
      },
      search: { subtitle: 'Open their file' },
    },

    modules: { crm: 'Customers' },
    assistant: {
      openers: {
        crm: {
          'how-is-the-customer-base': 'How is our customer base doing?',
          'which-prospects': 'Which prospects do we have right now?',
          'file-a-customer': 'File a customer I have just spoken to',
          'raise-a-credit-limit': "Raise a customer's credit limit",
        },
      },
    },
    permissions: {
      crm: {
        read: 'See the customer file',
        write: 'Create and edit customers and contacts',
        archive: 'Archive customers',
        import: 'Import customers from a spreadsheet',
      },
    },
    settings: {
      ns: {
        crm: {
          title: 'Customers',
          subtitle: 'What a new customer starts with',
          defaultCountry: { label: 'Country a new customer starts in' },
          defaultCurrency: {
            label: 'Currency a new customer is billed in',
            help: 'ISO 4217, three letters. It can be changed per customer afterwards.',
          },
          defaultPaymentTerms: { label: 'Payment terms a new customer starts with' },
          defaultPaymentMethod: { label: 'Payment method a new customer starts with' },
          defaultTaxRegime: {
            label: 'VAT regime a new customer starts under',
            help: 'Leave it on standard unless most of what you sell is exempt or intra-community.',
          },
          requireTaxId: {
            label: 'Require a tax id on every customer',
            help: 'Leave it off if prospects are filed before anybody has asked for theirs.',
          },
          defaultToIndividual: {
            label: 'New customers are private individuals by default',
            help: 'Turn it on if you sell to the public. Either way it can be changed on each customer.',
          },
        },
      },
    },
    audit: {
      actions: {
        crm: {
          customer_created: 'Customer added',
          customer_updated: 'Customer edited',
          customer_archived: 'Customer archived',
          customer_restored: 'Customer restored',
          contact_added: 'Contact added',
          contact_updated: 'Contact edited',
          contact_removed: 'Contact removed',
          customers_imported: 'Customers imported from a spreadsheet',
        },
      },
    },
  },

  fr: {
    crm: {
      title: 'Clients',
      subtitle: 'La fiche de chaque client à qui vous vendez, entreprise ou particulier',
      // La explicación entera del módulo. El TEXTO vive en `help/<idioma>.md`
      // —el mismo fichero que compila el manual del cliente—; aquí sólo van
      // las dos frases que el índice de la ayuda necesita antes de abrirlo.
      help: {
        title: 'Clients',
        subtitle: 'Ce que contient la fiche d’un client, pourquoi elle ne se supprime pas et d’où viennent ses chiffres',
      },
      dashboard: {
        activeCustomers: 'Clients actifs',
        activeCustomersHelp: 'Combien de fiches client sont actives en ce moment, hors fiches archivées.',
        pendingAmount: 'En attente d\'encaissement',
        latestCustomers: 'Derniers clients',
        latestCustomersHelp: 'Les fiches ouvertes le plus récemment, de la plus récente à la plus ancienne. Étirez la carte et il en tient plus.',
        pendingAmountHelp: 'Somme de ce qui a été facturé aux clients et pas encore encaissé, en euros.',
      },
      actions: {
        new: 'Nouveau client', import: 'Importer', edit: 'Modifier',
        archive: 'Archiver', cancel: 'Annuler', save: 'Enregistrer', remove: 'Supprimer', close: 'Fermer',
        restore: 'Restaurer',
      },
      filters: {
        search: 'Rechercher', searchPlaceholder: 'Nom, numéro fiscal, ville…',
        status: 'Statut', allStatuses: 'Tous',
        kind: 'Type', allKinds: 'Tous',
        sector: 'Secteur', allSectors: 'Tous',
        archived: 'Archivés', archivedNo: 'Sans archivés',
        archivedOnly: 'Archivés seulement', archivedAll: 'Tous',
      },
      status: { prospect: 'Prospect', active: 'Actif', inactive: 'Inactif' },
      kinds: { company: 'Entreprise', individual: 'Particulier' },
      // La liste mêle entreprises et particuliers : son en-tête ne peut pas
      // dire « Entreprise » au-dessus d'une colonne où il y a aussi des gens.
      table: {
        name: 'Client', sector: 'Secteur', city: 'Ville',
        contact: 'Contact', pending: 'Restant dû', status: 'Statut',
      },
      kpi: { active: 'Actifs', prospects: 'Prospects', total: 'Total', pending: 'Restant dû' },
      empty: {
        none: 'Aucun client pour le moment',
        noneHint: 'Crée le premier, ou importe ceux que vous avez déjà depuis un tableur.',
        filtered: 'Aucun résultat',
        filteredHint: "Essaie d'enlever un filtre ou de chercher autre chose.",
      },
      pager: { previous: 'Précédent', next: 'Suivant', of: 'Page {page} sur {pages}' },
      customer: {
        title: 'Fiche client',
        loading: 'Chargement…',
        details: 'Coordonnées',
        terms: 'Conditions commerciales',
        billingAddress: 'Adresse de facturation',
        shippingAddress: 'Adresse de livraison',
        differentShipping: 'La livraison va ailleurs',
        noCreditLimit: 'Sans plafond',
        fiscal: 'Fiscalité',
      },
      // Le secteur est stocké sous forme de code et affiché traduit. Un code
      // absent d'ici s'affiche tel quel : il vient d'un import qui n'a pas pu
      // être rattaché à la liste, et l'afficher est ce qui le fait corriger.
      paymentTerms: {
        immediate: 'Au comptant',
        prepaid: "Payé d'avance",
        on_delivery: 'À la livraison',
        '15_days': '15 jours',
        '30_days': '30 jours',
        '45_days': '45 jours',
        '60_days': '60 jours',
        '30_eom': '30 jours fin de mois',
        '60_eom': '60 jours fin de mois',
        '50_50': "50 % d'avance, 50 % à la livraison",
        other: 'Autre',
      },
      paymentMethods: {
        transfer: 'Virement bancaire',
        direct_debit: 'Prélèvement automatique',
        card: 'Carte',
        cash: 'Espèces',
        cheque: 'Chèque',
        promissory_note: 'Billet à ordre',
        confirming: 'Affacturage inversé',
        other: 'Autre',
      },
      taxRegimes: {
        general: 'Régime normal',
        equivalence_surcharge: 'Majoration d’équivalence',
        exempt: 'Exonérée de TVA',
        intra_community: 'Intracommunautaire',
        export: 'Exportation (hors UE)',
        reverse_charge: 'Autoliquidation',
      },
      sectors: {
        agriculture: 'Agriculture, élevage et pêche',
        automotive: 'Automobile',
        'banking-insurance': 'Banque et assurance',
        'chemicals-plastics': 'Chimie et plastiques',
        construction: 'Construction et travaux publics',
        consulting: 'Conseil et services professionnels',
        education: 'Éducation et formation',
        'energy-utilities': 'Énergie et services publics',
        'food-beverage': 'Agroalimentaire et boissons',
        'health-social': 'Santé et action sociale',
        hospitality: 'Hôtellerie et restauration',
        installations: 'Installations et maintenance',
        legal: 'Services juridiques',
        'logistics-transport': 'Logistique et transport',
        manufacturing: 'Industrie et fabrication',
        'marketing-media': 'Marketing, publicité et médias',
        metal: 'Métallurgie et mécanique',
        'pharma-biotech': 'Pharmacie et biotechnologie',
        'public-sector': 'Secteur public et administration',
        'real-estate': 'Immobilier',
        retail: 'Commerce de détail',
        'sports-leisure': 'Sport et loisirs',
        technology: 'Technologie et logiciels',
        'textile-fashion': 'Textile, mode et chaussure',
        'tourism-travel': 'Tourisme et voyages',
        wholesale: 'Commerce de gros et distribution',
        other: 'Autre',
      },
      fields: {
        taxRegime: 'Régime de TVA',
        taxRegimeHint: 'Détermine si la facture porte la TVA, et à quel taux',
        vatNumber: 'Numéro de TVA intracommunautaire',
        vatNumberHint: 'Uniquement si vous lui vendez depuis un autre pays de l’UE',
        vatNumberNeeded: 'Manquant : une vente intracommunautaire sans numéro de TVA ne tient pas en contrôle',
        iban: 'IBAN',
        ibanHint: 'Le compte du client, pour prélever les échéances',
        vatNumberPlaceholder: 'ESB12345678',
        ibanPlaceholder: 'ES91 2100 0418 4502 0005 1332',
        withholdingRate: 'Retenue à la source',
        withholdingRateHint: 'En pourcentage. 0 pour presque tous les clients',
        name: 'Raison sociale', namePlaceholder: "Nom complet de l'entreprise",
        personName: 'Nom et prénom', personNamePlaceholder: 'Nom complet de la personne',
        shortName: 'Nom court', shortNameHint: 'Celui que vous utilisez dans les listes',
        kind: 'Type de client', kindHint: "On ne demande ni secteur ni site web à un particulier",
        taxId: 'N° fiscal', personTaxId: "N° de pièce d'identité",
        status: 'Statut', sector: 'Secteur', noSector: 'Sans secteur',
        noPaymentTerms: 'Non défini', noPaymentMethod: 'Non défini',
        email: 'Courriel', phone: 'Téléphone', website: 'Site web',
        address: 'Rue', postalCode: 'Code postal', city: 'Ville', town: 'Ville', country: 'Pays',
        creditLimit: 'Plafond de crédit', creditLimitHint: "Laisse 0 si vous n'en avez pas fixé",
        paymentTerms: 'Conditions de paiement', paymentMethod: 'Moyen de paiement', currency: 'Devise',
        rating: 'Évaluation', noRating: 'Non évalué', notes: 'Notes',
        createdAt: 'Créé le', lastActivity: 'Dernière activité',
        contactName: 'Nom', contactPosition: 'Fonction',
        contactPositionPlaceholder: 'Ex. : Directeur technique',
      },
      tabs: { overview: 'Fiche', history: 'Historique' },
      movements: {
        title: 'Historique des mouvements',
        quote: { created: 'Devis créé', accepted: 'Devis accepté' },
        work_order: { created: "Ordre de travail ouvert", completed: 'Ordre de travail terminé', cancelled: 'Ordre de travail annulé' },
        invoice: { issued: 'Facture émise', payment_registered: 'Règlement enregistré', paid: 'Facture encaissée', cancelled: 'Facture annulée' },
      },
      activity: {
        quotes: 'Devis', accepted: 'Acceptés', openWorkOrders: 'OT ouvertes',
        invoiced: 'Facturé', pending: 'Restant dû',
      },
      contacts: {
        title: 'Interlocuteurs',
        add: 'Ajouter un contact', addTitle: 'Ajouter un contact', editTitle: 'Modifier le contact',
        none: 'Aucun contact pour le moment',
        noneHint: "La personne que vous appelez quand il se passe quelque chose chez ce client.",
        primary: 'Principal', makePrimary: 'Rendre principal', markPrimary: 'En faire le contact principal',
        primaryHint: "C'est le nom qui figure sur les documents. Il ne peut y en avoir qu'un.",
        firstIsPrimary: 'Le premier de la liste est le contact principal.',
        removeTitle: 'Supprimer le contact', removeBody: 'Supprimer {name} de ce client ?',
        removed: 'Contact supprimé', saved: 'Contact enregistré',
      },
      form: {
        newTitle: 'Nouveau client', editTitle: 'Modifier le client',
        company: "Coordonnées de l'entreprise", person: 'Coordonnées de la personne',
        newHint: 'Seul le nom est obligatoire ; le reste peut attendre',
        created: 'Client créé', saved: 'Client enregistré',
      },
      archive: {
        title: 'Archiver le client',
        body: 'Tu vas archiver {name}.',
        keeps: "Rien n'est supprimé : il disparaît des listes et son historique reste intact.",
        done: 'Client archivé',
        isArchived: 'Ce client est archivé : il n’apparaît sur aucune liste et ne peut pas être choisi sur un devis. Restaurez-le avec le bouton ci-dessus.',
        restored: 'Client de retour dans les listes',
      },
      import: {
        title: 'Importer des clients',
        file: 'Fichier', fileHint: 'Un tableur (.xlsx, .xls ou .csv) avec un client par ligne.',
        sheet: 'Feuille', sheetOption: '{name} ({rows} lignes)',
        mapHint: 'Indique quelle colonne est quoi. {rows} lignes seront importées.',
        ignore: 'Ne pas importer', column: 'Colonne {index}',
        preview: 'Premières lignes',
        duplicatesNote: 'Les lignes dont le numéro fiscal existe déjà sont ignorées, pas dupliquées.',
        run: 'Importer {rows} lignes',
        created: 'Créés', skipped: 'Ignorés', failed: 'En erreur',
        errorsTitle: 'Lignes non importées', row: 'Ligne {row} :',
        warningsTitle: 'Lignes importées sans une donnée', dropped: 'importée sans {field}, car celui de la feuille n’est pas valide',
        done: '{count} clients importés',
        nameRequired: 'Indique quelle colonne contient le nom du client',
        fields: {
          name: 'Nom ou raison sociale', shortName: 'Nom court', taxId: 'N° fiscal',
          email: 'Courriel', phone: 'Téléphone', website: 'Site web',
          address: 'Adresse', city: 'Ville', postalCode: 'Code postal', country: 'Pays',
          shippingAddress: 'Adresse de livraison', sector: 'Secteur', notes: 'Notes',
          iban: 'IBAN', vatNumber: 'Numéro de TVA intracommunautaire',
        },
      },
      search: { subtitle: 'Ouvrir sa fiche' },
    },

    modules: { crm: 'Clients' },
    assistant: {
      openers: {
        crm: {
          'how-is-the-customer-base': 'Comment va notre portefeuille de clients ?',
          'which-prospects': 'Quels prospects avons-nous en ce moment ?',
          'file-a-customer': "Enregistre un client avec qui je viens de parler",
          'raise-a-credit-limit': "Augmente la limite de crédit d'un client",
        },
      },
    },
    permissions: {
      crm: {
        read: 'Voir la fiche des clients',
        write: 'Créer et modifier clients et contacts',
        archive: 'Archiver des clients',
        import: 'Importer des clients depuis un tableur',
      },
    },
    settings: {
      ns: {
        crm: {
          title: 'Clients',
          subtitle: 'Avec quoi démarre un nouveau client',
          defaultCountry: { label: "Pays par défaut d'un nouveau client" },
          defaultCurrency: {
            label: 'Devise de facturation par défaut',
            help: 'ISO 4217, trois lettres. Modifiable ensuite client par client.',
          },
          defaultPaymentTerms: { label: 'Conditions de paiement par défaut' },
          defaultPaymentMethod: { label: 'Moyen de paiement par défaut' },
          defaultTaxRegime: {
            label: 'Régime de TVA par défaut d’un nouveau client',
            help: 'Laissez-le sur normal sauf si presque tout ce que vous vendez est exonéré ou intracommunautaire.',
          },
          defaultToIndividual: {
            label: 'Les nouveaux clients sont des particuliers par défaut',
            help: 'Active-le si vous vendez au grand public. Modifiable ensuite client par client.',
          },
          requireTaxId: {
            label: 'Exiger un numéro fiscal sur chaque client',
            help: "Laisse désactivé si vous créez des prospects avant de le demander.",
          },
        },
      },
    },
    audit: {
      actions: {
        crm: {
          customer_created: 'Client créé',
          customer_updated: 'Client modifié',
          customer_archived: 'Client archivé',
          customer_restored: 'Client restauré',
          contact_added: 'Contact ajouté',
          contact_updated: 'Contact modifié',
          contact_removed: 'Contact supprimé',
          customers_imported: 'Clients importés depuis un tableur',
        },
      },
    },
  },
}

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'
// Lo que dicen las tarjetas del asistente (backend/assistant.js): otro contrato,
// en su propio fichero por la misma razón.
import { withAssistantTools } from './i18n/assistant.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

export const messages = withToolLabels(withAssistantTools(withAnalytics(baseMessages, 'crm'), 'crm'))

export default { routes, navigation, messages }
